import type { Deficit } from './close-day';
import { HISTORY } from './config';
import { coverageFor, hasEnoughCoverage } from './coverage';
import { daysBetween } from './dates';
import { NUTRIENTS } from './nutrients';
import { scaleNutrients, sumNutrients } from './portions';
import type { MealEntry, Targets } from './types';

export interface DeficitInput {
  /** El día que se quiere cerrar, 'YYYY-MM-DD'. */
  date: string;
  /** Registros de hoy y de los días anteriores (lo que sobre se ignora). */
  meals: readonly MealEntry[];
  /** Días marcados como completos. */
  completeDates: ReadonlySet<string>;
  targets: Targets;
}

/**
 * Lo que le falta a hoy para cerrar el día (SPEC 7.1.1).
 *
 * Macros: contra el objetivo del día. Micros: lo que falta para que la media
 * de la semana llegue al objetivo, contando SOLO hoy y los días marcados como
 * completos de los últimos 7. Un día sin registrar o a medias no es un día de
 * cero (null ≠ 0): antes, el primer día de uso faltaban 7·110 − 60 = 710 mg de
 * vitamina C en vez de 50.
 *
 * La cobertura (< 70 % = datos insuficientes, SPEC 7.1.2) se mira sobre los
 * mismos días que cuentan, no solo sobre hoy.
 */
export function computeDeficits({ date, meals, completeDates, targets }: DeficitInput): Deficit[] {
  const today = meals.filter((m) => m.date === date);
  const week = meals.filter((m) => {
    const ago = daysBetween(m.date, date);
    return (
      m.date === date || (ago > 0 && ago < HISTORY.recentWindowDays && completeDates.has(m.date))
    );
  });
  const days = new Set(week.map((m) => m.date)).add(date).size;

  const totals = (entries: readonly MealEntry[]) =>
    sumNutrients(entries.map((m) => scaleNutrients(m.per100g, m.grams)));
  const todayTotals = totals(today);
  const weekTotals = totals(week);

  const out: Deficit[] = [];
  for (const meta of NUTRIENTS) {
    if (meta.key === 'kcal' || meta.key === 'sodium' || meta.key === 'saturatedFat') continue;

    const target =
      meta.key === 'protein'
        ? targets.proteinG
        : meta.key === 'carbs'
          ? targets.carbsG
          : meta.key === 'fat'
            ? targets.fatG
            : targets.micros[meta.key];
    if (!target || target <= 0) continue;

    const daily = meta.window === 'dia';
    if (!hasEnoughCoverage(coverageFor(daily ? today : week, meta.key))) continue;

    const missing = daily
      ? target - (todayTotals[meta.key] ?? 0)
      : target * days - (weekTotals[meta.key] ?? 0);
    if (missing <= 0) continue;

    // El peso es la fracción del objetivo que falta: un hueco del 80 % importa
    // más que uno del 10 %, y así no hace falta una tabla de pesos a mano.
    out.push({ key: meta.key, missing, weight: Math.min(missing / target, 1) });
  }

  return out;
}
